<template>
  <div
    class="loanable-details"
    :class="{
      'fixed-width': fixedWidth,
    }"
    :style="enableViewTransition ? { 'view-transition-name': 'loanable-image' } : {}"
  >
    <header class="loanable-details__header">
      <loanable-carousel :loanable="loanable" show-sharing-mode show-owner />
    </header>

    <main class="loanable-details__content">
      <pretty-description small :loanable="loanable" class="loanable-details__description" />

      <markdown-content
        v-if="loanable.comments && loanable.comments.length > 0"
        :content="loanable.comments"
        :max-height="100"
        class="mx-2 mb-2"
      >
        <template #moreLink
          ><b-link @click="() => createLoan(tabs.loanable)">{{
            $t("components.loanable.details.more")
          }}</b-link></template
        >
      </markdown-content>

      <div v-if="showEstimate" class="mx-2 mb-2">
        <simple-price
          :label="$t('components.loan.price_summary.estimated_cost')"
          :loading="searchingLoanables || !loanable.estimated"
          :value="loanableEstimate ? Math.abs(loanableEstimate) : 0"
        />
      </div>
    </main>

    <footer v-if="!basic" class="loanable-details__footer">
      <!-- Only one button will be displayed at a time. -->
      <icon-button
        v-if="loanable.available"
        variant="success"
        :disabled="(loanable.type === 'car' || loanable.type === 'car_trailer') && !canBorrowCar"
        size="sm"
        @click="() => createLoan()"
      >
        {{ $t("components.loanable.details.select") }}
      </icon-button>
      <icon-button
        v-else-if="!loanable.tested"
        v-b-tooltip.hover
        variant="outline-success"
        :disabled="invalidDuration"
        :loading="searchingLoanables"
        size="sm"
        @click.stop.prevent="$emit('test')"
      >
        {{ $t("components.loanable.details.check_availability") }}
      </icon-button>
      <div v-else class="text-center">
        <div
          v-if="
            loanable.min_loan_duration_in_minutes !== null &&
            searchDuration < loanable.min_loan_duration_in_minutes
          "
          class="text-muted small mb-2"
        >
          {{
            $t("loanables.loan_duration.min.message", {
              duration: duration(loanable.min_loan_duration_in_minutes),
            })
          }}
        </div>
        <div
          v-if="
            loanable.max_loan_duration_in_minutes !== null &&
            searchDuration > loanable.max_loan_duration_in_minutes
          "
          class="text-muted small mb-2"
        >
          {{
            $t("loanables.loan_duration.max.message", {
              duration: duration(loanable.max_loan_duration_in_minutes),
            })
          }}
        </div>
        <icon-button variant="outline-info" size="sm" @click="() => createLoan(tabs.calendar)">{{
          $t("components.loanable.details.view_availabilities")
        }}</icon-button>
      </div>
    </footer>
  </div>
</template>

<script>
import { tabs } from "@/components/Loan/FullLoanSections";
import PrettyDescription from "@/components/Loanable/PrettyDescription.vue";
import IconButton from "@/components/shared/IconButton.vue";
import MarkdownContent from "@/components/shared/MarkdownContent.vue";
import LoanableCarousel from "@/components/Loanable/LoanableCarousel.vue";
import SimplePrice from "@/components/Loan/SimplePrice.vue";

import { duration } from "@/helpers/filters";
import { canBorrowCar } from "@/helpers/permissions/users";

export default {
  name: "LoanableDetails",
  components: {
    IconButton,
    LoanableCarousel,
    MarkdownContent,
    PrettyDescription,
    SimplePrice,
  },
  props: {
    // Whether to display only the images and description list
    basic: {
      type: Boolean,
      default: false,
    },
    fixedWidth: {
      type: Boolean,
      default: false,
    },
    loanable: {
      type: Object,
      required: false,
      default: undefined,
    },
    showEstimate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    canBorrowCar() {
      return canBorrowCar(this.$store.state.user);
    },
    enableViewTransition() {
      return !this.$store.state["loanable.search"].newLoanModalOpened && this.selected;
    },
    invalidDuration() {
      return this.searchDuration <= 0;
    },
    loanableEstimate() {
      return this.$store.state["loanable.search"].loanableEstimates[this.loanable.id];
    },
    searchDuration() {
      return this.$store.state["loanable.search"].searchDuration;
    },
    searchingLoanables() {
      return this.$store.state["loanable.search"].availabilitiesLoading || this.loanable.testing;
    },
    selected() {
      return this.$route.query.loanable_id == this.loanable.id;
    },
    tabs() {
      return tabs;
    },
  },
  methods: {
    createLoan(tab) {
      this.$emit("select", { tab });
    },
    duration,
  },
};
</script>

<style lang="scss">
.loanable-details {
  font-size: 0.875rem;
  position: relative;
  overflow-x: hidden;
  background: white;
  border-radius: 1rem;
  box-shadow: $small-shadow;
  display: flex;
  flex-direction: column;
  container-type: inline-size;

  &__content {
    flex-grow: 1;
  }

  &__description {
    padding: 0.5rem 0;
  }

  &.fixed-width {
    width: 16rem;
  }

  &__header {
    position: relative;
    width: 100%;
  }

  &__footer {
    position: sticky;
    bottom: 0;
    width: 100%;
    padding: 0.5rem;
    display: flex;
    justify-content: space-around;
    align-items: center;
    background: white;
    border-top: 1px solid $light-grey;
  }
}
</style>
